import React, { useState, useEffect, useCallback } from 'react';
import { MenuItem as MenuItemType, MenuOption } from '../types/menu';
import { useCart } from '../contexts/CartContext';
import { CartItem, CartOption, CartOptionItem } from '../types/cart';
import ImageWithFallback from './ImageWithFallback';
import { useNavigate } from 'react-router-dom';
import { getLocalizedValue, getCurrentLanguage } from '../config/languageConfig';
import { getTranslation } from '../config/translations';
import {
  SupportedCurrency,
  getCurrencyFromLanguage,
  getPriceInfo
} from '../utils/currencyUtils';
import { useOperationTimeCheck } from '../hooks/useOperationTime';
import { useAddressAffordance } from '../contexts/AddressAffordanceContext';
import ServiceClosedModal from './ServiceClosedModal';
import { getLogoMonoPath } from '../utils/assetPath';

interface RestaurantNames {
  name: string;
  nameEn: string | null;
  nameKo: string | null;
  nameJa: string | null;
  nameZhCn: string | null;
  nameZhTw: string | null;
}

interface MenuItemProps {
  menuItem: MenuItemType;
  restaurantId: string;
  restaurantName: string;
  restaurantMultiNames?: RestaurantNames;
  currency?: SupportedCurrency;
}

// 메뉴 옵션을 장바구니 옵션으로 변환하는 함수
const convertMenuOptionToCartOption = (menuOption: MenuOption): CartOption => {
  const optionItems: CartOptionItem[] = menuOption.menuOptionItems.map(item => ({
    id: item.id,
    name: item.name,
    price: item.price // 원화(KRW) 그대로 사용
  }));

  return {
    id: menuOption.id,
    name: menuOption.name,
    price: 0, // 옵션 자체 가격은 0으로 설정
    optionItems: optionItems
  };
};

const MenuItem: React.FC<MenuItemProps> = ({ 
  menuItem, 
  restaurantId, 
  restaurantName,
  restaurantMultiNames,
  currency
}) => {
  const { cart, addToCart, updateItemQuantity, canceledItemId } = useCart();
  const navigate = useNavigate();
  const [showServiceClosedModal, setShowServiceClosedModal] = useState(false);
  const { checkAndExecute } = useOperationTimeCheck();
  const { checkAddressAndExecute } = useAddressAffordance();
  
  // 현재 언어 설정에 따라 이름과 설명 처리
  const currentLang = getCurrentLanguage();

  // 품절 여부 (available이 명시적으로 false인 경우)
  const isSoldOut = menuItem.available === false;
  const soldOutLabel = getTranslation('menu.soldOut', currentLang);
  
  // 현재 언어에 기반한 통화 설정 (props로 전달된 currency가 없는 경우)
  const effectiveCurrency = currency || getCurrencyFromLanguage(currentLang);
  
  // 프로모션 정보 가져오기
  const priceInfo = getPriceInfo(menuItem, effectiveCurrency);
  
  // 현재 언어에 맞는 메뉴 이름 가져오기
  const displayName = getLocalizedValue(menuItem.name, {
    en: menuItem.nameEn
  });
  
  // 현재 언어에 맞는 설명 가져오기
  let displayDescription = getLocalizedValue(menuItem.description, {
    en: menuItem.descriptionEn
  });
  
  // 설명 텍스트에서 대괄호 제거
  // 대괄호로 감싸진 형태인 경우 대괄호를 제거하고 안의 내용만 추출
  if (displayDescription.match(/^\[(.*)\]$/)) {
    displayDescription = displayDescription.replace(/^\[(.*)\]$/, '$1');
  }
  
  // 유효한 설명인지 확인 (빈 문자열이거나 [] 형태가 아닌 경우)
  const hasValidDescription = displayDescription && !displayDescription.match(/^\[]$/);
  
  // 장바구니에서 현재 아이템의 수량 찾기
  const getCartQuantity = useCallback((): number => {
    if (!cart) return 0;
    
    // 현재 표시중인 레스토랑과 카트에 저장된 레스토랑이 다르면 0 반환
    if (cart.restaurantId !== restaurantId) return 0;
    
    const cartItem = cart.items.find(item => item.id === menuItem.id);
    return cartItem ? cartItem.quantity : 0;
  }, [cart, menuItem.id, restaurantId]);
  
  // 현재 수량 상태 관리
  const [quantity, setQuantity] = useState<number>(getCartQuantity());
  
  // 카트 상태가 변경될 때마다 수량 동기화
  useEffect(() => {
    setQuantity(getCartQuantity());
  }, [cart, getCartQuantity]);
  
  // 취소된 아이템 감지하여 수량 초기화
  useEffect(() => {
    if (canceledItemId && canceledItemId === menuItem.id) {
      setQuantity(getCartQuantity());
    }
  }, [canceledItemId, menuItem.id, getCartQuantity]);
  
  // 수량 증가 (주소 체크 + 운영시간 체크)
  const increaseQuantity = () => {
    // 품절 메뉴는 장바구니에 담을 수 없음
    if (isSoldOut) return;

    checkAddressAndExecute(() => {
      checkAndExecute(
        () => {
          // 운영 중일 때 - 기존 로직 실행
          let startQuantity = quantity;
          if (cart && cart.restaurantId !== restaurantId && quantity === 0) {
            startQuantity = 0;
          }
          
          const newQuantity = startQuantity + 1;
          setQuantity(newQuantity);
          updateCartItem(newQuantity);
        },
        () => setShowServiceClosedModal(true) // 운영시간 외일 때
      );
    }, 'cart');
  };
  
  // 수량 감소 (운영시간 체크)
  const decreaseQuantity = () => {
    if (quantity > 0) {
      checkAndExecute(
        () => {
          // 운영 중일 때 - 기존 로직 실행
          const newQuantity = quantity - 1;
          setQuantity(newQuantity);
          updateCartItem(newQuantity);
        },
        () => setShowServiceClosedModal(true) // 운영시간 외일 때
      );
    }
  };
  
  // 장바구니 업데이트
  const updateCartItem = (newQuantity: number) => {
    // 수량이 0이면 아이템 제거 (updateItemQuantity에서 자동으로 처리됨)
    if (newQuantity === 0) {
      updateItemQuantity(menuItem.id, 0);
    } 
    // 처음 추가하는 경우 (장바구니에 해당 아이템이 없거나 다른 레스토랑 카트가 있을 경우)
    else if (!cart || cart.restaurantId !== restaurantId || !cart.items.find(item => item.id === menuItem.id)) {
      // 메뉴 옵션을 장바구니 옵션으로 변환
      const cartOptions: CartOption[] = menuItem.menuOptions.map(option => 
        convertMenuOptionToCartOption(option)
      );
      
      // 장바구니 아이템 생성
      const cartItem: CartItem = {
        id: menuItem.id,
        name: displayName,
        price: priceInfo.hasPromotion ? priceInfo.promotionPrice : priceInfo.regularPrice, // 프로모션 가격 적용
        quantity: newQuantity,
        options: cartOptions,
        image: menuItem.image,
        // 다국어 메뉴 이름 추가 - null/undefined 체크하여 할당
        nameEn: menuItem.nameEn || undefined,
        // 다음은 MenuItem에 정의되지 않아 제거함
        // nameJa: menuItem.nameJa,
        // nameZhCn: menuItem.nameZhCn,
        // nameZhTw: menuItem.nameZhTw,
        // 모든 통화별 가격 정보 저장
        priceKrw: menuItem.priceKrw,
        priceUsd: menuItem.priceUsd,
        priceJpy: menuItem.priceJpy,
        priceCny: menuItem.priceCny,
        priceTwd: menuItem.priceTwd,
        // 프로모션 정보 저장
        promotionRatio: menuItem.promotionRatio,
        promotionPrice: menuItem.promotionPrice,
        promotionPriceKrw: menuItem.promotionPriceKrw,
        promotionPriceUsd: menuItem.promotionPriceUsd,
        promotionPriceJpy: menuItem.promotionPriceJpy,
        promotionPriceCny: menuItem.promotionPriceCny,
        promotionPriceTwd: menuItem.promotionPriceTwd,
        isPromotionKrw: menuItem.isPromotionKrw,
        isPromotionUsd: menuItem.isPromotionUsd,
        isPromotionJpy: menuItem.isPromotionJpy,
        isPromotionCny: menuItem.isPromotionCny,
        isPromotionTwd: menuItem.isPromotionTwd
      };
      
      // 가게 다국어 이름 정보를 저장할 객체
      const restaurantInfo = {
        // restaurantName은 현재 언어에 맞는 이름
        restaurantName: restaurantName,
        // 다국어 이름 정보가 있으면 모두 추가 (null, undefined 검사 추가)
        restaurantNameEn: restaurantMultiNames?.nameEn || restaurantName,
        restaurantNameJa: restaurantMultiNames?.nameJa || restaurantMultiNames?.nameEn || restaurantName,
        restaurantNameZhCn: restaurantMultiNames?.nameZhCn || restaurantMultiNames?.nameEn || restaurantName,
        restaurantNameZhTw: restaurantMultiNames?.nameZhTw || restaurantMultiNames?.nameEn || restaurantName,
        restaurantNameKo: restaurantMultiNames?.nameKo || restaurantMultiNames?.nameEn || restaurantName
      };
      
      // 검증: restaurantName이 displayName과 같은 경우(이름이 잘못 할당된 경우) 로그 출력
      if (restaurantInfo.restaurantName === displayName) {
        console.warn('Warning: Restaurant name is identical to menu item name!', {
          restaurantName: restaurantInfo.restaurantName,
          menuItemName: displayName
        });
      }
      
      // CartContext의 addToCart 호출 - 가게 정보 객체도 함께 전달
      addToCart(restaurantId, restaurantInfo, cartItem);
    } 
    // 기존 아이템 수량 업데이트 (같은 레스토랑일 경우에만)
    else if (cart && cart.restaurantId === restaurantId) {
      updateItemQuantity(menuItem.id, newQuantity);
    }
  };
  
  // 메뉴 상세 페이지로 이동
  const handleViewMenuDetail = () => {
    navigate(`/restaurant/${restaurantId}/menu/${menuItem.id}`);
  };
  
  return (
    <>
      <div className="flex flex-col py-2 hover:bg-gray-50 transition-colors">
        <div className="flex items-stretch relative">
          {/* 메뉴 이미지 */}
          <div 
            className="w-20 h-20 bg-gray-100 rounded-md overflow-hidden flex-shrink-0 mr-3 my-auto cursor-pointer hover:opacity-80 transition-opacity relative"
            onClick={handleViewMenuDetail}
          >
            <ImageWithFallback
              src={menuItem.image || ''}
              alt={displayName}
              className={`w-full h-full object-cover ${isSoldOut ? 'opacity-40 grayscale' : ''}`}
              fallback={getLogoMonoPath()}
            />
            {isSoldOut && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <span className="text-white text-xs font-bold">{soldOutLabel}</span>
              </div>
            )}
          </div>
          
          {/* 메뉴 정보 */}
          <div className={`flex-grow flex flex-col justify-between py-1 pr-10 ${isSoldOut ? 'opacity-50' : ''}`}>
            <div 
              className="cursor-pointer"
              onClick={handleViewMenuDetail}
            >
              <h3 className="text-base font-semibold mb-1 text-left line-clamp-1 hover:text-red-600 transition-colors">{displayName}</h3>
              {hasValidDescription && (
                <p className="text-gray-500 text-xs line-clamp-1 text-left hover:text-gray-700 transition-colors">{displayDescription}</p>
              )}
            </div>
            
            {/* 가격, 수량 조절 */}
            <div className="mt-3">
              <div className="flex items-center justify-between">
                {/* 가격 */}
                <div className="font-semibold text-base flex flex-col items-start">
                  {priceInfo.hasPromotion ? (
                    <>
                      {/* 원래 가격 (위에 작게) */}
                      <div className="text-gray-400 text-xs line-through">
                        {priceInfo.formattedRegularPrice}
                      </div>
                      {/* 할인율과 할인 가격 */}
                      <div className="flex items-center space-x-2">
                        <span className="text-red-500 font-bold text-sm">{priceInfo.formattedDiscountRate}</span>
                        <span className="text-black font-bold text-base">{priceInfo.formattedPromotionPrice}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="text-black font-bold text-base">{priceInfo.formattedRegularPrice}</span>
                    </>
                  )}
                </div>
                
                {/* 수량 조절 또는 품절 표시 */}
                {isSoldOut ? (
                  <span className="px-3 py-1 rounded-md bg-gray-100 text-gray-500 text-sm font-semibold">
                    {soldOutLabel}
                  </span>
                ) : (
                  <div className="flex items-center">
                    <button
                      className={`w-7 h-7 flex items-center justify-center border border-gray-300 rounded-md bg-white ${quantity > 0 ? 'text-gray-700' : 'text-gray-400'}`}
                      onClick={decreaseQuantity}
                      disabled={quantity === 0}
                      aria-label="Decrease quantity"
                    >
                      <span className="text-lg font-medium">−</span>
                    </button>
                    <span className="mx-2 text-base font-medium min-w-[20px] text-center">{quantity}</span>
                    <button
                      className={`w-7 h-7 flex items-center justify-center border rounded-md ${
                        quantity > 0 
                          ? 'bg-red-500 text-white border-red-500' 
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                      onClick={increaseQuantity}
                      aria-label="Increase quantity"
                    >
                      <span className="text-lg font-medium">+</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* 메뉴 상세보기 버튼 - Material Design 스타일 */}
          <div 
            className="absolute right-0 inset-y-0 flex items-center justify-center w-10 transition-colors hover:bg-gray-100 active:bg-gray-200 rounded-r cursor-pointer"
            role="button"
            aria-label="View menu details"
            onClick={handleViewMenuDetail}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-gray-400">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
            </svg>
          </div>
        </div>
      </div>

      {/* 운영시간 외 모달 */}
      <ServiceClosedModal 
        isOpen={showServiceClosedModal}
        onClose={() => setShowServiceClosedModal(false)}
      />
    </>
  );
};

export default MenuItem;
